import React from 'react'
import { useState, useCallback } from 'react'
import { format } from 'date-fns'
import { useAppDispatch } from '../../../redux/store'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import { requestsService } from '../../../services/requestsService'
import { BaseResponse } from '../../../types/base-response'
import UserErrorMessage from '../../../utils/errorFilter'

export interface ShiftData {
  hours: number
  startTime: Date
  desc: string
  success: boolean
}

interface UseShiftLengthResult {
  shiftData: ShiftData | undefined
  setShiftData: React.Dispatch<React.SetStateAction<ShiftData | undefined>>
  getShiftLength: (employeeId: number, shiftDate: Date) => Promise<ShiftData | undefined>
}

export const useShiftLength = (): UseShiftLengthResult => {
  const dispatch = useAppDispatch()
  
  const [shiftData, setShiftData] = useState<ShiftData | undefined>()

  const getShiftLength = useCallback(
    async (employeeId: number, shiftDate: Date): Promise<ShiftData | undefined> => {
      try {
        const data = await requestsService.getShiftDescription(
          employeeId,
          format(shiftDate, 'yyyy-MM-dd hh-mm-ss').slice(0, 10)
        )
        
        const result: ShiftData = {
          hours: data.shiftMinutes / 60,
          startTime: data.startTime,
          desc: data.shiftDescription ? data.shiftDescription : '9.00 - 17.30',
          success: true,
        }
        
        setShiftData(result)
        return result
      } catch (err: any) {
        if (err.response?.status === 404) {
          const fallbackResult: ShiftData = {
            hours: 8,
            startTime: new Date(),
            desc: '9.00 - 17.30',
            success: false,
          }
          
          setShiftData(fallbackResult)
          return fallbackResult
        }
        
        const response: BaseResponse = err.response?.data
        response?.errors?.forEach(error => {
          dispatch(showErrorMessage(React.createElement(UserErrorMessage, { name: error.name })))
        })
        
        return undefined
      }
    },
    [dispatch]
  )

  return {
    shiftData,
    setShiftData,
    getShiftLength
  }
}